/**
 * SelectInstanceForInboxDialog - Modal premium para vincular instância a uma inbox
 * 
 * ARQUITETURA:
 * Este componente é usado na aba "Inboxes" para vincular uma instância disponível
 * a uma inbox que ainda não possui instância vinculada.
 * 
 * DIFERENÇA DO LinkInstanceDialog:
 * - LinkInstanceDialog: usado na aba "Instâncias" (recebe instance, busca inboxes elegíveis)
 * - SelectInstanceForInboxDialog: usado na aba "Inboxes" (recebe inbox, busca instâncias elegíveis)
 * 
 * FLUXO:
 * 1. Usuário clica "Vincular Instância" em uma inbox
 * 2. Modal abre mostrando instâncias disponíveis (mesmo channel_type, status 'released', sem inbox)
 * 3. Usuário seleciona instância e clica em "Vincular"
 * 4. Sistema vincula via RPC link_instance_to_inbox
 * 
 * DESIGN: Estilo premium inspirado em Stripe/Linear
 * 
 * @author Sistema Multi-Provider
 * @version 2.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Server, PackageX, Link2, Check, Zap, AlertTriangle, ArrowLeftRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useInstances } from '@/hooks/useInstances';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const logger = createLogger('SelectInstanceForInboxDialog');

/**
 * Props do componente SelectInstanceForInboxDialog
 */
interface SelectInstanceForInboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID da inbox que receberá a instância */
  inboxId: string;
  /** Nome da inbox para exibição */
  inboxName: string;
  /** Tipo de canal da inbox (para filtrar instâncias compatíveis) */
  channelType: string;
  /** Slug do provider da inbox (filtro mais preciso que channel_type) */
  providerSlug?: string;
  /** ID do tenant */
  tenantId: string;
  /** Callback executado após vincular com sucesso */
  onSuccess: () => void;
}

/**
 * Interface para instância elegível
 */
interface EligibleInstance {
  id: string;
  instance_name: string;
  status: string;
  channel_type: string;
  provider_slug: string;
  provider_display_name: string;
}

/**
 * Modal premium para selecionar e vincular uma instância a uma inbox
 */
export function SelectInstanceForInboxDialog({
  open,
  onOpenChange,
  inboxId,
  inboxName,
  channelType,
  providerSlug,
  tenantId,
  onSuccess,
}: SelectInstanceForInboxDialogProps) {
  // Estados locais
  const [eligibleInstances, setEligibleInstances] = useState<EligibleInstance[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  // Hook de instâncias para a função de link
  const { linkInstance } = useInstances(tenantId);

  /**
   * Busca instâncias elegíveis para vincular
   * Critérios: mesmo channel_type, status 'released', sem inbox vinculada
   */
  useEffect(() => {
    if (!open || !tenantId || (!channelType && !providerSlug)) {
      setEligibleInstances([]);
      setSelectedInstanceId(null);
      return;
    }

    const fetchEligibleInstances = async () => {
      setLoading(true);
      try {
        logger.debug('Buscando instâncias elegíveis', { channelType, providerSlug, tenantId });

        // v2.1.0 — Trata WhatsApp como grupo unificado: 'whatsapp' (não-oficial)
        // e 'whatsapp_oficial' (Meta Cloud API) são intercambiáveis para fins de
        // vinculação. Isso permite trocar o motor (provider) da inbox dentro do
        // mesmo grupo de canal — espelha a lógica do MigrateProviderDialog.
        const isWhatsAppGroup =
          channelType === 'whatsapp' || channelType === 'whatsapp_oficial';

        // @ts-ignore - evitando erro de tipo excessivamente profundo
        let query = supabase
          .from('provider_instances')
          .select(`
            id, 
            instance_name, 
            status, 
            inbox_id, 
            provider_id,
            api_providers!inner(id, slug, display_name, channel_type)
          `)
          .eq('tenant_id', tenantId)
          .eq('status', 'released')
          .is('inbox_id', null);

        // Filtro por canal: WhatsApp unificado OU canal exato.
        // O providerSlug NÃO é mais usado para restringir (era ele que travava
        // em "mesmo provider"); agora é usado apenas para destacar visualmente
        // instâncias de outro motor com badge "Outro motor".
        if (isWhatsAppGroup) {
          query = query.in('api_providers.channel_type', ['whatsapp', 'whatsapp_oficial'] as any);
        } else {
          query = query.eq('api_providers.channel_type', channelType as any);
        }

        const { data, error } = await query;

        if (error) {
          logger.error('Erro ao buscar instâncias', error);
          throw error;
        }

        // Formata dados para exibição, extraindo info do provider via JOIN
        const instances: EligibleInstance[] = (data || []).map((inst: any) => ({
          id: inst.id,
          instance_name: inst.instance_name,
          status: inst.status,
          channel_type: inst.api_providers?.channel_type || '',
          provider_slug: inst.api_providers?.slug || '',
          provider_display_name: inst.api_providers?.display_name || '',
        }));

        logger.debug('Instâncias elegíveis encontradas', { count: instances.length });
        setEligibleInstances(instances);
      } catch (error) {
        logger.error('Erro ao carregar instâncias', error);
        setEligibleInstances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEligibleInstances();
  }, [open, channelType, providerSlug, tenantId]);

  /**
   * Handler para vincular instância selecionada
   */
  const handleLink = async () => {
    if (!selectedInstanceId || !inboxId) return;

    setLinking(true);
    try {
      logger.info('Vinculando instância à inbox', { instanceId: selectedInstanceId, inboxId });
      
      await linkInstance({
        instanceId: selectedInstanceId,
        inboxId: inboxId,
      });

      // Fecha o dialog e notifica sucesso
      onOpenChange(false);
      setSelectedInstanceId(null);
      onSuccess();
    } catch (error) {
      logger.error('Erro ao vincular instância', error);
      // Toast já exibido pelo hook useInstances
    } finally {
      setLinking(false);
    }
  };

  /**
   * Renderiza skeleton loading premium
   */
  const renderLoadingState = () => (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div 
          key={i} 
          className="p-4 rounded-xl border border-border/50 bg-card/50"
        >
          <div className="flex items-start gap-4">
            <Skeleton className="h-10 w-10 rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-24 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  /**
   * Computa label amigável para o canal exibido no empty state.
   * Para o grupo WhatsApp, descreve as duas variantes suportadas.
   */
  const channelDisplayLabel =
    channelType === 'whatsapp' || channelType === 'whatsapp_oficial'
      ? 'WhatsApp (não-oficial e Cloud API)'
      : channelType;

  /**
   * Indica se há ao menos uma instância elegível pertencente a um provider
   * diferente do atual da inbox — usado para mostrar o aviso de troca de motor.
   */
  const hasOtherEngineInstance =
    !!providerSlug &&
    eligibleInstances.some((i) => i.provider_slug !== providerSlug);

  /**
   * Renderiza estado vazio premium
   */
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      {/* Ícone grande em container suave */}
      <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
        <PackageX className="h-8 w-8 text-muted-foreground" />
      </div>
      
      {/* Texto hierárquico */}
      <h4 className="font-medium text-foreground mb-1">
        Nenhuma instância disponível
      </h4>
      <p className="text-sm text-muted-foreground max-w-[280px]">
        Não há instâncias liberadas para o tipo de canal <span className="font-medium text-foreground/80">{channelDisplayLabel}</span>
      </p>
      
      {/* Dica de ação */}
      <div className="mt-4 text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
        💡 Crie ou libere uma instância na aba "Instâncias"
      </div>
    </div>
  );

  /**
   * Renderiza card de instância premium
   */
  const renderInstanceCard = (instance: EligibleInstance) => {
    const isSelected = selectedInstanceId === instance.id;
    // Sinaliza visualmente quando a instância pertence a um motor (provider)
    // diferente do atual da inbox — avisa o usuário que vincular trocará o motor.
    const isOtherEngine = !!providerSlug && instance.provider_slug !== providerSlug;

    return (
      <div
        key={instance.id}
        onClick={() => setSelectedInstanceId(instance.id)}
        className={cn(
          "relative p-4 rounded-xl border cursor-pointer",
          "transition-all duration-200 ease-out",
          "hover:shadow-md hover:border-primary/40",
          isSelected 
            ? "ring-2 ring-primary border-primary/60 bg-primary/5 shadow-sm" 
            : "border-border/60 bg-card hover:bg-accent/30"
        )}
      >
        {/* Radio indicator oculto mas funcional */}
        <RadioGroupItem 
          value={instance.id} 
          id={instance.id} 
          className="sr-only"
        />
        
        <div className="flex items-start gap-4">
          {/* Ícone em container premium */}
          <div className={cn(
            "h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0",
            "transition-colors duration-200",
            isSelected 
              ? "bg-primary text-primary-foreground" 
              : "bg-primary/10 text-primary"
          )}>
            <Server className="h-5 w-5" />
          </div>
          
          {/* Conteúdo principal */}
          <div className="flex-1 min-w-0">
            {/* Linha 1: Nome + Badge Status */}
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="font-semibold text-foreground truncate">
                {instance.instance_name}
              </span>
              
              {/* Badge de disponível premium */}
              <Badge 
                variant="outline"
                className={cn(
                  "text-[10px] font-medium px-2 py-0.5 gap-1 flex-shrink-0",
                  "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                  "border-emerald-500/30"
                )}
              >
                <Check className="h-3 w-3" />
                Disponível
              </Badge>
            </div>
            
            {/* Linha 2: Provider + ID */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge 
                variant="secondary" 
                className="text-[10px] font-medium bg-secondary/60"
              >
                <Zap className="h-2.5 w-2.5 mr-1" />
                {instance.provider_display_name}
              </Badge>

              {/* Badge "Outro motor": sinaliza migração de provider ao vincular */}
              {isOtherEngine && (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px] font-medium px-2 py-0.5 gap-1",
                    "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                    "border-amber-500/30"
                  )}
                >
                  <ArrowLeftRight className="h-2.5 w-2.5" />
                  Outro motor
                </Badge>
              )}

              <span className="text-[10px] text-muted-foreground font-mono tracking-wider">
                #{instance.id.substring(0, 8).toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Indicador de seleção visual */}
          {isSelected && (
            <div className="absolute top-3 right-3 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
        {/* Header Premium */}
        <div className="px-6 pt-6 pb-4 border-b border-border/50">
          <div className="flex items-start gap-4">
            {/* Ícone em container premium */}
            <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Link2 className="h-5 w-5 text-primary" />
            </div>
            
            {/* Texto do header */}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-foreground leading-tight">
                Vincular Instância
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Selecione uma instância para vincular à inbox{' '}
                <Badge variant="outline" className="text-xs font-medium ml-1 align-middle">
                  {inboxName}
                </Badge>
              </p>
            </div>
          </div>
        </div>

        {/* Aviso não-bloqueante: troca de motor disponível */}
        {!loading && hasOtherEngineInstance && (
          <div className="mx-6 mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Selecionar uma instância de <strong>outro motor</strong> migrará automaticamente o motor da inbox e suas credenciais.
            </span>
          </div>
        )}

        {/* Corpo do modal */}
        <div className="px-6 py-4">
          {loading ? (
            renderLoadingState()
          ) : eligibleInstances.length === 0 ? (
            renderEmptyState()
          ) : (
            <ScrollArea className="max-h-[320px] -mx-2 px-2">
              <RadioGroup
                value={selectedInstanceId || ''}
                onValueChange={setSelectedInstanceId}
                className="space-y-3"
              >
                {eligibleInstances.map(renderInstanceCard)}
              </RadioGroup>
            </ScrollArea>
          )}
        </div>

        {/* Footer Premium */}
        <DialogFooter className="px-6 py-4 border-t border-border/50 bg-muted/20 gap-3 sm:gap-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={linking}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedInstanceId || linking || loading}
            className="min-w-[120px] gap-2 transition-all duration-200 hover:shadow-md"
          >
            {linking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Vinculando...</span>
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                <span>Vincular</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
